import { actionCreators, actionTypes } from '@app/common/auth/auth.reducer';
import { actionTypes as sessionActionTypes } from '@app/common/session/session.reducer';
import { AnyAction, Middleware } from 'redux';
import { PERSIST, REHYDRATE } from 'redux-persist';
import { LOCATION_CHANGE, CALL_HISTORY_METHOD } from 'connected-react-router';
import * as cognito from '../../lib/cognito';
import UserSession from '@app/common/user-session';
import { State } from '../root-reducer';
import { add, differenceInMinutes } from 'date-fns';

// Track token creation time 
const tokenCreationTimes = new Map<string, number>();

const IGNORE_LIST = [
  PERSIST,
  REHYDRATE,
  LOCATION_CHANGE,
  CALL_HISTORY_METHOD,
  actionTypes.TOKEN_REFRESH_REQUEST,
  actionTypes.TOKEN_REFRESH_SUCCESS,
  actionTypes.TOKEN_REFRESH_FAILURE,
];

export const jwt: Middleware = ({ dispatch, getState }) => {
  let tokenRefreshing = false;
  let actionQueue: AnyAction[] = [];
  let refreshCheckInterval: NodeJS.Timeout | null = null;
  let refreshRetryCount = 0;
  const MAX_REFRESH_RETRIES = 3;

  // Helper function to check if we're on an auth page
  const isOnAuthPage = () => {
    const currentPath = window.location.pathname;
    const basePath = process.env.REACT_APP_BASE_PATH || '';
    const authPaths = [
      `${basePath}/auth/login`,
      `${basePath}/login`,
      `${basePath}/auth/forgot-password`,
      `${basePath}/forgot-password`,
      `${basePath}/auth/new-password`,
      `${basePath}/new-password`
    ];
    
    return authPaths.some(path => currentPath === path || currentPath.endsWith(path));
  };

  // Setup proactive token refresh checking
  const setupProactiveRefresh = () => {
    if (refreshCheckInterval) {
      clearInterval(refreshCheckInterval);
    }
    
    refreshCheckInterval = setInterval(() => {
      // Don't refresh tokens if user is on authentication pages
      if (isOnAuthPage()) {
        return;
      }
      
      const state = getState() as State;
      const session = state.common?.auth?.session;
      
      if (session?.token && session?.expiration) {
        const currentTime = new Date();
        const expireTime = new Date(session.expiration as number);
        
        // Get the token's total lifetime in minutes (from expiration timestamp)
        const tokenId = session.userId || 'default';
        const creationTime = tokenCreationTimes.get(tokenId) || currentTime.getTime();
        const totalMinutesValid = Math.floor((expireTime.getTime() - creationTime) / (1000 * 60));
        
        // Use 20% of the token's total lifetime as buffer, with minimum of 2 minutes
        const bufferMinutes = Math.max(Math.floor(totalMinutesValid * 0.2), 2);
        
        const minutesUntilExpire = differenceInMinutes(expireTime, currentTime);
        
        // If token will expire within the buffer time and we're not already refreshing
        if (minutesUntilExpire <= bufferMinutes && !tokenRefreshing) {
          tokenRefreshing = true;
          refreshRetryCount = 0;
          dispatch(actionCreators.tokenRefreshRequest());
        }
      }
    }, 60000); // Check every minute
  };

  return (next) => (action) => {
    // Setup the interval on first action
    if (!refreshCheckInterval) {
      setupProactiveRefresh();
    }

    if (!IGNORE_LIST.includes(action.type)) {
      // Don't refresh tokens if user is on authentication pages
      if (isOnAuthPage()) {
        return next(action);
      }
      
      const session = (getState() as State).common?.auth?.session;
      const currentTime = add(new Date(), { minutes: 2 }); // Increased buffer to 2 minutes
      const expireTime = new Date(session?.expiration as number);

      if (!session?.token || currentTime < expireTime) {
        return next(action);
      } else {
        if (!tokenRefreshing) {
          tokenRefreshing = true;
          refreshRetryCount = 0;
          dispatch(actionCreators.tokenRefreshRequest());
        }
        actionQueue.push(action);
      }
    } else {
      if (action.type === REHYDRATE && action.payload?.common?.auth?.session) {
        const session = action.payload.common.auth.session;
        if (session.userId) {
          cognito.createCognitoUser(session.userId);
          
          // Only refresh these session params if not in a partner space
          if (!UserSession.getParam('isImpersonating')) {
            UserSession.setParams({
              token: session.token,
              role: session.role,
              userId: session.userId,
              partnerName: session.partnerName,
              partnerId: session.partnerId,
            });
          }
          
          // Start proactive refresh after rehydration
          setupProactiveRefresh();
        }
      }
      
      // When we get a new token, record its creation time
      const state = getState() as State;
      const session = state.common?.auth?.session;
      
      if (session && session.userId) {
        // Store the token creation time in our Map
        const now = new Date().getTime();
        tokenCreationTimes.set(session.userId, now);
      }
      
      if (action.type === actionTypes.TOKEN_REFRESH_SUCCESS) {
        tokenRefreshing = false;
        refreshRetryCount = 0;
        actionQueue.forEach(dispatch);
        actionQueue = [];
      } else if (action.type === actionTypes.TOKEN_REFRESH_FAILURE) {
        if (refreshRetryCount < MAX_REFRESH_RETRIES) {
          refreshRetryCount++;
          
          setTimeout(() => {
            dispatch(actionCreators.tokenRefreshRequest());
          }, 1000 * refreshRetryCount);
        } else {
          tokenRefreshing = false;
          refreshRetryCount = 0;
          actionQueue = [];
          dispatch(actionCreators.logoutRequest());
        }
      } else if (action.type === actionTypes.LOGOUT_SUCCESS && refreshCheckInterval) {
        clearInterval(refreshCheckInterval);
        refreshCheckInterval = null;
        
        // Clean up token creation time on logout
        const session = (getState() as State).common?.auth?.session;
        if (session?.userId) {
          tokenCreationTimes.delete(session.userId);
        }
      }
      action.type !== actionTypes.TOKEN_REFRESH_REQUEST && next(action);
    }
  };
};
